import React, { ErrorInfo, ReactNode } from 'react';
import {
  Box, Button, Code, Heading, Text, VStack,
} from '@chakra-ui/react';
import { reportClientError } from '../utils/clientErrorReporter'; // item 1525-web

// Top-level error boundary (item 91): a render-time exception in any one screen
// (a null-deref in a grid row, a transient broken module, …) used to white-out
// the ENTIRE app — there was no boundary. Now it degrades to a readable message
// with a reload, so one bad screen never takes the whole app down.

interface ErrorBoundaryProps { children: ReactNode, resetKey?: unknown }
interface ErrorBoundaryState {
  hasError: boolean, message?: string, stack?: string, lastResetKey?: unknown,
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, lastResetKey: props.resetKey };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, message: error.message };
  }

  // clear the error when the route changes (resetKey = pathname) so a transient
  // crash — e.g. a wallet extension mutating the DOM — doesn't need a full reload;
  // navigating away (or back) re-renders the screen fresh
  static getDerivedStateFromProps(props: ErrorBoundaryProps, state: ErrorBoundaryState) {
    if (props.resetKey !== state.lastResetKey) {
      return {
        lastResetKey: props.resetKey,
        ...(state.hasError ? { hasError: false, message: undefined, stack: undefined } : {}),
      };
    }
    return null;
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // surface it for debugging
    // eslint-disable-next-line no-console
    console.error('Unhandled UI error:', error, info.componentStack);
    // item 1525-web — the "real logging": the same reporter window.onerror feeds (production only,
    // capped, swallowed). A render crash never reaches window.onerror, so the boundary reports it.
    reportClientError(error, 'ErrorBoundary');
    // also stash the component stack so it shows ON the card (item 91 — no console
    // digging needed to localize the failing component)
    this.setState({ stack: `${error.stack ?? error.message}\n\nComponent stack:${info.componentStack}` });
  }

  render() {
    const { hasError, message, stack } = this.state;
    const { children } = this.props;
    if (!hasError) return children;
    return (
      <Box minH="60vh" display="flex" alignItems="center" justifyContent="center" p={8}>
        <VStack spacing={4} maxW="640px" textAlign="center">
          <Heading size="md">Something went wrong on this screen</Heading>
          <Text fontSize="sm" color="gray.500">
            The rest of the app is fine — reload to try again. If it keeps happening, let us know
            what you were doing.
          </Text>
          {message && (
            <Text fontSize="xs" color="gray.400" fontFamily="mono" noOfLines={2}>
              {message}
            </Text>
          )}
          <Button onClick={() => window.location.reload()}>Reload</Button>
          {stack && (
            <Box as="details" w="full" textAlign="left" mt={2}>
              <Box as="summary" cursor="pointer" fontSize="xs" color="gray.500" mb={2}>
                Show technical details (for the dev)
              </Box>
              <Code
                display="block"
                whiteSpace="pre-wrap"
                fontSize="0.7rem"
                p={3}
                borderRadius="md"
                maxH="320px"
                overflowY="auto"
                w="full"
              >
                {stack}
              </Code>
            </Box>
          )}
        </VStack>
      </Box>
    );
  }
}

(ErrorBoundary as React.ComponentClass<ErrorBoundaryProps>).defaultProps = {
  resetKey: undefined,
};

export default ErrorBoundary;
